import { PausableTimeout } from 'widgets/toolbox/PausableTimeout';
import { TWidget } from 'widgets/Widget';

export type TGlobalAlertMessages = {
    error500: string;
}

/**
 * @description Base GlobalAlerts implementation.
 * @param Widget Base widget for extending
 * @returns GlobalAlerts class
 */
function GlobalAlertsClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory global
     * @class GlobalAlerts
     * @augments Widget
     * @classdesc Global Alerts with accessibility alert role possibility.
     * Acts as a popup with delayed disappearing. Popup will render user action description.
     * @property {boolean} data-widget - Widget name `globalAlerts`
     * @property {number} data-timeout - Value of hide timeout in milliseconds
     * @property {object} data-global-alerts - Global alerts localization messages
     * Possible values are: `error500`
     *
     * @example <caption>Global Alerts widget</caption>
     * <div
     *     class="b-global_alerts ${isAccessibilityAlertsHidden ? 'm-visually_hidden' : ''}"
     *     data-widget="globalAlerts"
     *     data-tau="global_alerts"
     *     data-global-alerts='{
     *         "error500": "${Resource.msg('alert.error500', 'global', null)}"
     *     }'
     *     aria-live="polite"
     * >
     *     <div
     *         hidden="hidden"
     *         data-ref="item"
     *         class="b-global_alerts-item"
     *         role="alert"
     *         data-event-mouseover="pauseAlert"
     *         data-event-mouseout="resumeAlert"
     *         data-tau="global_alerts_item"
     *     ></div>
     * </div>
     */
    class GlobalAlerts extends Widget {
        pausableTimeout: PausableTimeout | undefined;

        prefs() {
            return {
                timeout: 4000,
                errorClassName: 'm-error',
                globalAlerts: <TGlobalAlertMessages>{},
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization, onDestroy hook to clear pausable timeout.
         * Listening `show.alert` event to display a relevant Global Alert
         * @listens "alert.show"
         * @listens "alert.error"
         */
        init() {
            super.init();
            this.onDestroy(() => this.disposePausableTimeout());
            /**
             * @description Event for Global Alert, contains data to be shown inside Global Alert popover
             * @event "alert.show"
             */
            this.eventBus().on('alert.show', 'showAlert');
            /**
             * @description Event for Global Alert, contains data to be shown inside Global Alert popover as an error message
             * @event "alert.error"
             */
            this.eventBus().on('alert.error', 'showError');
        }

        /**
         * @description Shows global errors like 500 response
         * @param data - event message data
         * @param data.errorCode - error code string that could be mapped to standard global error message
         * @param data.accessibilityAlert - alert message
         */
        showError(data: TResponseError) {
            if (!data) {
                return;
            }

            let message = '';

            if (data.errorCode) {
                message = this.prefs().globalAlerts[`error${data.errorCode}`];
            } else if (data.accessibilityAlert) {
                message = data.accessibilityAlert;
            }

            this.showAlert({ accessibilityAlert: message }, 'error');
        }

        /**
         * @description Dispose pausable timeout and clear it resources
         */
        disposePausableTimeout() {
            if (this.pausableTimeout) {
                this.pausableTimeout.destroy();

                this.pausableTimeout = undefined;
            }
        }

        /**
         * @description Show Global Alert by starting pausable timeout
         * @param data - event data
         * @param data.accessibilityAlert - Message to be shown and pronounced
         * @param type - type of the message
         */
        showAlert(data: {accessibilityAlert?: string}, type?: string) {
            if (data && data.accessibilityAlert) {
                if (this.shown) {
                    this.hideAlert();
                }

                if (type === 'error') {
                    this.ref('item').addClass(this.prefs().errorClassName);
                }

                this.ref('item')
                    .setText(data.accessibilityAlert)
                    .show();
                this.shown = true;

                this.pausableTimeout = new PausableTimeout(() => this.hideAlert(), this.prefs().timeout);
            }
        }

        /**
         * @description Hide Global Alert and clear timeout
         */
        hideAlert() {
            this.ref('item')
                .removeClass(this.prefs().errorClassName)
                .setText('')
                .hide();
            this.shown = false;
            this.disposePausableTimeout();
        }

        /**
         * @description Pauses Global Alert displaying.
         * Pauses hide alert with pausable timeout.
         */
        pauseAlert() {
            if (this.pausableTimeout) {
                this.pausableTimeout.pause();
            }
        }

        /**
         * @description Resumes Global Alert displaying.
         * Resumes hide alert with pausable timeout.
         */
        resumeAlert() {
            if (this.pausableTimeout) {
                this.pausableTimeout.resume();
            }
        }
    }

    return GlobalAlerts;
}

export type TGlobalAlerts = ReturnType<typeof GlobalAlertsClassCreator>;

export type TGlobalAlertsInstance = InstanceType<TGlobalAlerts>;

export default GlobalAlertsClassCreator;
